import { useId, useMemo, useState } from 'react'
import { AutomationRule, AutomationRuleFormData, TriggerType, ConditionOperator } from '../types/automationRule'
import { getConditionFieldOptions, getConditionValueSuggestions } from '../utils/automationRuleConditionCatalog'
import './AutomationRuleForm.css'

const TRIGGER_LABELS: Record<TriggerType, string> = {
  'lab-result-created': 'Lab result created',
  'lab-result-updated': 'Lab result updated',
  'medication-review-due': 'Medication review due',
  'appointment-scheduled': 'Appointment scheduled',
  'consultation-completed': 'Consultation completed',
  'vital-sign-abnormal': 'Vital sign abnormal',
}

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'Equals',
  notEquals: 'Does not equal',
  contains: 'Contains',
  lessThanOrEqual: 'On or before / ≤',
  greaterThanOrEqual: 'On or after / ≥',
}

interface AutomationRuleFormProps {
  initialData?: AutomationRule
  onSubmit: (data: AutomationRuleFormData) => void
  onCancel: () => void
  isLoading?: boolean
}

export function AutomationRuleForm({ initialData, onSubmit, onCancel, isLoading }: AutomationRuleFormProps) {
  const [formData, setFormData] = useState<AutomationRuleFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    triggerType: initialData?.triggerType || 'lab-result-created',
    enabled: initialData?.enabled ?? true,
    condition: initialData?.condition || {
      field: '',
      operator: 'equals',
      value: '',
    },
    action: initialData?.action || {
      type: 'create-task',
      title: '',
      description: '',
      priority: 'medium',
      urgency: 'routine',
      ehrComponentType: '',
    },
  })

  const fieldListId = useId()
  const valueListId = useId()
  const fieldOptions = useMemo(
    () => getConditionFieldOptions(formData.triggerType),
    [formData.triggerType]
  )
  const valueSuggestions = useMemo(
    () =>
      getConditionValueSuggestions(
        formData.triggerType,
        formData.condition.field,
        formData.condition.operator
      ),
    [formData.triggerType, formData.condition.field, formData.condition.operator]
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  const triggerTypes: TriggerType[] = [
    'lab-result-created',
    'lab-result-updated',
    'medication-review-due',
    'appointment-scheduled',
    'consultation-completed',
    'vital-sign-abnormal',
  ]

  const operators: ConditionOperator[] = ['equals', 'notEquals', 'contains', 'lessThanOrEqual', 'greaterThanOrEqual']

  const priorities: Array<'low' | 'medium' | 'high' | 'urgent'> = ['low', 'medium', 'high', 'urgent']
  const urgencies: Array<'routine' | 'urgent' | 'stat' | 'asap'> = ['routine', 'urgent', 'stat', 'asap']

  const conditionHint =
    valueSuggestions.length > 0
      ? 'Field and value suggestions match the selected trigger; you can still type custom names. Common values appear as you type.'
      : 'Use the field name from the event payload. For on/before or on/after comparisons, you can use the value now for the current date and time.'

  return (
    <form className="automation-rule-form" onSubmit={handleSubmit}>
      <section className="arf-section" aria-labelledby="arf-details-heading">
        <h2 id="arf-details-heading" className="arf-section__title">
          Rule details
        </h2>
        <div className="arf-stack">
          <div className="form-group">
            <label htmlFor="name">
              Rule name <span className="required">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., Critical lab value alert"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="What this rule does (optional)"
              rows={3}
            />
          </div>
        </div>
      </section>

      <section className="arf-section arf-panel" aria-labelledby="arf-when-heading">
        <h2 id="arf-when-heading" className="arf-section__title">
          When to evaluate
        </h2>
        <p id="arf-when-lead" className="arf-section__lead">
          Choose the event that loads this rule, and whether it is active.
        </p>
        <div className="arf-trigger-row">
          <div className="form-group arf-trigger-row__main">
            <label htmlFor="triggerType">
              Trigger <span className="required">*</span>
            </label>
            <select
              id="triggerType"
              value={formData.triggerType}
              onChange={(e) => setFormData({ ...formData, triggerType: e.target.value as TriggerType })}
              required
              aria-describedby="arf-when-lead"
            >
              {triggerTypes.map((type) => (
                <option key={type} value={type}>
                  {TRIGGER_LABELS[type]}
                </option>
              ))}
            </select>
          </div>

          <div className="arf-toggle">
            <input
              id="ruleEnabled"
              className="arf-toggle__input"
              type="checkbox"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
            />
            <label htmlFor="ruleEnabled" className="arf-toggle__label">
              <span className="arf-toggle__text">Rule enabled</span>
              <span className="arf-toggle__hint">Inactive rules are never run</span>
            </label>
          </div>
        </div>
      </section>

      <fieldset className="arf-fieldset arf-panel">
        <legend className="arf-fieldset__legend">Condition</legend>
        <p className="arf-section__lead arf-fieldset__lead">
          A task is created when the event data meets all of the following.
        </p>

        <div className="arf-condition-grid">
          <div className="form-group">
            <label htmlFor="conditionField">
              Field <span className="required">*</span>
            </label>
            <input
              id="conditionField"
              type="text"
              list={fieldListId}
              autoComplete="off"
              value={formData.condition.field}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  condition: { ...formData.condition, field: e.target.value },
                })
              }
              required
              placeholder="e.g. reviewStatus"
              aria-describedby="arf-condition-hint"
            />
            <datalist id={fieldListId}>
              {fieldOptions.map((o) => (
                <option key={o.field} value={o.field}>
                  {o.label}
                </option>
              ))}
            </datalist>
          </div>

          <div className="form-group arf-condition-grid__operator">
            <label htmlFor="conditionOperator">
              Operator <span className="required">*</span>
            </label>
            <select
              id="conditionOperator"
              value={formData.condition.operator}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  condition: { ...formData.condition, operator: e.target.value as ConditionOperator },
                })
              }
              required
              aria-describedby="arf-condition-hint"
            >
              {operators.map((op) => (
                <option key={op} value={op}>
                  {OPERATOR_LABELS[op]}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="conditionValue">
              Value <span className="required">*</span>
            </label>
            <input
              id="conditionValue"
              type="text"
              list={valueSuggestions.length > 0 ? valueListId : undefined}
              autoComplete="off"
              value={String(formData.condition.value ?? '')}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  condition: { ...formData.condition, value: e.target.value },
                })
              }
              required
              placeholder="e.g. in-review or now"
              aria-describedby="arf-condition-hint"
            />
            {valueSuggestions.length > 0 && (
              <datalist id={valueListId}>
                {valueSuggestions.map((v) => (
                  <option key={v} value={v} />
                ))}
              </datalist>
            )}
          </div>
        </div>

        <p id="arf-condition-hint" className="arf-hint-box">
          {conditionHint}
        </p>
      </fieldset>

      <section className="arf-section arf-panel" aria-labelledby="arf-action-heading">
        <h2 id="arf-action-heading" className="arf-section__title">
          Task to create
        </h2>
        <p className="arf-section__lead">These fields define the automated clinical task.</p>

        <div className="arf-stack">
          <div className="form-group">
            <label htmlFor="actionTitle">
              Task title <span className="required">*</span>
            </label>
            <input
              id="actionTitle"
              type="text"
              value={formData.action.title}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  action: { ...formData.action, title: e.target.value },
                })
              }
              required
              placeholder="e.g., Follow up on lab result"
            />
          </div>

          <div className="form-group">
            <label htmlFor="actionDescription">Task description</label>
            <textarea
              id="actionDescription"
              value={formData.action.description || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  action: { ...formData.action, description: e.target.value },
                })
              }
              placeholder="Shown on the task (optional)"
              rows={3}
            />
          </div>

          <div className="arf-two-col">
            <div className="form-group">
              <label htmlFor="actionPriority">
                Priority <span className="required">*</span>
              </label>
              <select
                id="actionPriority"
                value={formData.action.priority}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    action: { ...formData.action, priority: e.target.value as any },
                  })
                }
                required
              >
                {priorities.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="actionUrgency">Urgency</label>
              <select
                id="actionUrgency"
                value={formData.action.urgency || 'routine'}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    action: { ...formData.action, urgency: e.target.value as any },
                  })
                }
              >
                {urgencies.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="arf-two-col">
            <div className="form-group">
              <label htmlFor="ehrComponentType">EHR component type</label>
              <input
                id="ehrComponentType"
                type="text"
                value={formData.action.ehrComponentType || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    action: { ...formData.action, ehrComponentType: e.target.value },
                  })
                }
                placeholder="e.g. lab-result, medication"
              />
            </div>

            <div className="form-group">
              <label htmlFor="dueDateOffset">Due in (days from trigger)</label>
              <input
                id="dueDateOffset"
                type="number"
                min={0}
                value={formData.action.dueDateOffset ?? 0}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    action: { ...formData.action, dueDateOffset: parseInt(e.target.value, 10) || 0 },
                  })
                }
                placeholder="0"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="form-actions">
        <button type="button" className="btn-cancel" onClick={onCancel} disabled={isLoading}>
          Cancel
        </button>
        <button type="submit" className="btn-submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : initialData ? 'Update rule' : 'Create rule'}
        </button>
      </div>
    </form>
  )
}

